import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { findNewsBySlug, newsSummary, newsHeading, newsBullets, newsDateLabel } from "./news";
import { Seo } from "../../shared/Seo";
import { NewsArticleJsonLd, BreadcrumbJsonLd } from "../../shared/JsonLd";
import { useLandscapePhone } from "../../shared/useResponsive";
import { NewsReader } from "./NewsPage";
import styles from "./NewsPage.module.scss";
import { markNewsSeen } from "../../shared/announcement";

const SITE_URL = "https://achtungroyale.io";

// A single news post at /news/:slug: its own URL with article meta + JSON-LD, so
// individual announcements get rich link previews. `slug` is passed directly at
// prerender time; at runtime it comes from the route param.
export default function NewsPostPage({ slug: slugProp }: { slug?: string }) {
  const params = useParams();
  const slug = slugProp ?? params.slug ?? "";
  const post = findNewsBySlug(slug);
  const app = useLandscapePhone();
  useEffect(() => { if (post) markNewsSeen(slug); }, [post, slug]);

  if (!post) {
    return (
      <div className="section">
        <Seo
          title="News · Achtung Royale"
          description="Updates and patch notes for Achtung Royale."
          canonical={`${SITE_URL}/news/`}
        />
        <p className="app-muted">That post couldn’t be found.</p>
        <Link className={styles.newsBack} to="/news">
          ← All news
        </Link>
      </div>
    );
  }

  const url = `${SITE_URL}/news/${slug}/`;
  const summary = newsSummary(post);
  const heading = newsHeading(post);
  return (
    <div className={app ? `section ${styles.readerHost}` : "section"}>
      <Seo
        title={`${heading} · Achtung Royale`}
        description={summary}
        canonical={url}
        ogType="article"
      />
      <NewsArticleJsonLd
        title={heading}
        description={summary}
        datePublished={post.date}
        url={url}
      />
      <BreadcrumbJsonLd
        items={[
          { name: "Home", url: `${SITE_URL}/` },
          { name: "News", url: `${SITE_URL}/news` },
          { name: heading, url },
        ]}
      />
      {/* A landscape phone reads it in the two-pane reader, opened on this post. */}
      {app ? <NewsReader slug={slug} /> : (
        <>
          {/* The same ledger entry as on the list, on its own. */}
          <div className={styles.ledger}>
            <section className={styles.group}>
              <div className={styles.ver}>
                {post.version ? (
                  <span className={styles.verNum}>v{post.version}</span>
                ) : post.tag ? (
                  <span className={styles.verNum}>{post.tag}</span>
                ) : null}
                <time className={styles.verDate} dateTime={post.date}>{newsDateLabel(post.date)}</time>
                <span className={styles.verRule} />
              </div>
              <article className={styles.post}>
                {post.title ? <h3 className={styles.postTitle}>{post.title}</h3> : null}
                <ul className={styles.bullets}>
                  {newsBullets(post).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </article>
            </section>
          </div>
          <Link className={styles.newsBack} to="/news">
            ← All news
          </Link>
        </>
      )}
    </div>
  );
}
